import { useState } from "react";
import { Plus, PencilSimple, Bank, CreditCard, PiggyBank, ChartLineUp, Wallet, CurrencyBtc, House } from "@phosphor-icons/react";
import EntityDialog from "./EntityDialog";
import { CategoryIcon } from "./CategoryManagerDialog";
import { useCategories } from "../../lib/useCategories";
import {
  fmtMoney,
  ACCOUNT_TYPE_LABELS,
  DEBT_TYPE_LABELS,
  FREQUENCY_LABELS,
  INCOME_SOURCE_LABELS,
  CATEGORY_LABELS,
} from "../../lib/finance-format";

const ACCOUNT_ICONS = {
  checking: Bank,
  savings: PiggyBank,
  investment: ChartLineUp,
  retirement: ChartLineUp,
  cash: Wallet,
  crypto: CurrencyBtc,
  credit_card: CreditCard,
  real_estate: House,
  other: Bank,
};

const asOptions = (map) => Object.entries(map).map(([value, label]) => ({ value, label }));

// ---------- Shared row wrapper ----------
function EntityRow({ children, onClick, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="w-full text-left flex items-center gap-3 p-3 rounded-lg bg-[#0F1017] border border-white/6 hover:border-white/15 hover:bg-[#141621] transition group"
    >
      {children}
    </button>
  );
}

function Panel({ title, subtitle, count, addLabel, onAdd, children, testId }) {
  return (
    <section
      data-testid={testId}
      className="rounded-lg border border-white/8 bg-black/20 p-4 md:p-5"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/40">
            {subtitle}
          </div>
          <h3 className="text-base md:text-lg font-heading font-bold text-white">
            {title} <span className="text-white/40 tabular-nums font-mono text-sm ml-1">{count}</span>
          </h3>
        </div>
        <button
          data-testid={`${testId}-add`}
          onClick={onAdd}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-heading font-bold text-xs transition"
        >
          <Plus size={12} weight="bold" /> {addLabel}
        </button>
      </div>
      <div className="flex flex-col gap-2">{children}</div>
    </section>
  );
}

function EmptyState({ hint }) {
  return (
    <div className="py-8 text-center text-xs font-mono text-white/30">
      {hint}
    </div>
  );
}

// ---------- Accounts ----------
export function AccountsPanel({ accounts, scope, onChange }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const schema = [
    { key: "name", label: "Nombre", type: "text", required: true, full: true, placeholder: "Chase Checking" },
    { key: "account_type", label: "Tipo", type: "select", options: asOptions(ACCOUNT_TYPE_LABELS), default: "checking" },
    { key: "scope", label: "Ámbito", type: "select", options: [{value:"personal",label:"Personal"},{value:"business",label:"Negocio"}], default: scope === "business" ? "business" : "personal" },
    { key: "institution", label: "Institución", type: "text", placeholder: "Chase, Ally..." },
    { key: "balance", label: "Balance", type: "number", default: 0, hint: "Positivo. Para tarjetas = saldo adeudado." },
    { key: "apr", label: "APR (solo tarjeta)", type: "number", placeholder: "27.24", hint: "0-100" },
    { key: "credit_limit", label: "Límite (tarjeta)", type: "number", placeholder: "15000" },
    { key: "notes", label: "Notas", type: "textarea", full: true },
  ];

  const openCreate = () => { setEditing(null); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setOpen(true); };

  return (
    <Panel
      title="Cuentas"
      subtitle="ASSETS & LIABILITIES"
      count={accounts.length}
      addLabel="Cuenta"
      onAdd={openCreate}
      testId="accounts-panel"
    >
      {accounts.length === 0 && <EmptyState hint="Agrega tu primera cuenta." />}
      {accounts.map((a) => {
        const Icon = ACCOUNT_ICONS[a.account_type] || Bank;
        const isCC = a.account_type === "credit_card";
        const needsBalance = a.needs_balance_confirmation === true;
        return (
          <EntityRow key={a.account_id} onClick={() => openEdit(a)} testId={`account-row-${a.account_id}`}>
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
              style={{ backgroundColor: a.color, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.18)" }}
            >
              <Icon size={18} weight="fill" className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-sm font-heading font-bold text-white truncate">{a.name}</div>
                <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${a.scope === "business" ? "bg-purple-500/20 text-purple-300" : "bg-white/8 text-white/50"}`}>
                  {a.scope === "business" ? "NEG" : "PER"}
                </span>
                {needsBalance && (
                  <span
                    data-testid={`account-needs-balance-${a.account_id}`}
                    title="El saldo real no venía en el documento. Actualízalo manualmente o conecta este banco por Plaid."
                    className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-500/25 text-amber-800 dark:text-amber-200 border border-amber-500/50"
                  >
                    SIN SALDO
                  </span>
                )}
              </div>
              <div className="text-[11px] font-mono text-white/50 truncate">
                {ACCOUNT_TYPE_LABELS[a.account_type] || a.account_type}
                {a.institution ? ` · ${a.institution}` : ""}
                {isCC && a.apr ? ` · APR ${a.apr.toFixed(2)}%` : ""}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className={`font-mono font-bold tabular-nums ${needsBalance ? "text-amber-800 dark:text-amber-300" : isCC ? "text-red-400" : "text-white"}`}>
                {needsBalance ? "—" : `${isCC ? "-" : ""}${fmtMoney(a.balance)}`}
              </div>
              <PencilSimple size={11} weight="bold" className="ml-auto mt-1 opacity-0 group-hover:opacity-60 transition text-white/60" />
            </div>
          </EntityRow>
        );
      })}
      <EntityDialog
        open={open}
        onOpenChange={setOpen}
        entity={editing}
        schema={schema}
        endpoint="/finance/accounts"
        idField="account_id"
        title={editing ? "Editar cuenta" : "Nueva cuenta"}
        defaults={{ scope: scope === "business" ? "business" : "personal", color: "#2563EB" }}
        onSaved={onChange}
        onDeleted={onChange}
      />
    </Panel>
  );
}

// ---------- Debts ----------
export function DebtsPanel({ debts, scope, onChange }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const schema = [
    { key: "name", label: "Nombre", type: "text", required: true, full: true, placeholder: "Préstamo estudiantil" },
    { key: "debt_type", label: "Tipo", type: "select", options: asOptions(DEBT_TYPE_LABELS), default: "student" },
    { key: "scope", label: "Ámbito", type: "select", options: [{value:"personal",label:"Personal"},{value:"business",label:"Negocio"}], default: scope === "business" ? "business" : "personal" },
    { key: "lender", label: "Acreedor", type: "text", placeholder: "Navient..." },
    { key: "balance", label: "Saldo actual", type: "number", required: true, hint: "Cuánto debes hoy" },
    { key: "principal", label: "Principal original", type: "number", hint: "Monto original del préstamo" },
    { key: "apr", label: "APR %", type: "number", placeholder: "6.8", required: true, hint: "Tasa anual" },
    { key: "minimum_payment", label: "Pago mínimo", type: "number", placeholder: "340", hint: "Mensual" },
    { key: "interest_paid_ytd", label: "Interés pagado YTD", type: "number", placeholder: "0" },
    { key: "notes", label: "Notas", type: "textarea", full: true },
  ];

  const openCreate = () => { setEditing(null); setOpen(true); };
  const openEdit = (d) => { setEditing(d); setOpen(true); };

  return (
    <Panel
      title="Deudas"
      subtitle="LOANS · LONG TERM"
      count={debts.length}
      addLabel="Deuda"
      onAdd={openCreate}
      testId="debts-panel"
    >
      {debts.length === 0 && <EmptyState hint="Sin deudas. Todo bajo control." />}
      {debts.map((d) => (
        <EntityRow key={d.debt_id} onClick={() => openEdit(d)} testId={`debt-row-${d.debt_id}`}>
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
            style={{ backgroundColor: d.color, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.18)" }}
          >
            <CreditCard size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <div className="text-sm font-heading font-bold text-white truncate">{d.name}</div>
              <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${d.scope === "business" ? "bg-purple-500/20 text-purple-300" : "bg-white/8 text-white/50"}`}>
                {d.scope === "business" ? "NEG" : "PER"}
              </span>
            </div>
            <div className="text-[11px] font-mono text-white/50 truncate">
              {DEBT_TYPE_LABELS[d.debt_type] || d.debt_type}
              {d.lender ? ` · ${d.lender}` : ""}
              {d.minimum_payment ? ` · min ${fmtMoney(d.minimum_payment)}/mes` : ""}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="font-mono font-bold tabular-nums text-red-400">
              -{fmtMoney(d.balance)}
            </div>
            <div className={`text-[10px] font-mono font-bold tabular-nums ${d.apr >= 15 ? "text-red-400" : d.apr >= 8 ? "text-yellow-400" : "text-emerald-400"}`}>
              APR {d.apr?.toFixed(2)}%
            </div>
          </div>
        </EntityRow>
      ))}
      <EntityDialog
        open={open}
        onOpenChange={setOpen}
        entity={editing}
        schema={schema}
        endpoint="/finance/debts"
        idField="debt_id"
        title={editing ? "Editar deuda" : "Nueva deuda"}
        defaults={{ scope: scope === "business" ? "business" : "personal", color: "#EF4444" }}
        onSaved={onChange}
        onDeleted={onChange}
      />
    </Panel>
  );
}

// ---------- Incomes ----------
export function IncomesPanel({ incomes, scope, onChange }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const schema = [
    { key: "name", label: "Nombre", type: "text", required: true, full: true, placeholder: "Salario Google" },
    { key: "source_type", label: "Fuente", type: "select", options: asOptions(INCOME_SOURCE_LABELS), default: "salary" },
    { key: "scope", label: "Ámbito", type: "select", options: [{value:"personal",label:"Personal"},{value:"business",label:"Negocio"}], default: scope === "business" ? "business" : "personal" },
    { key: "amount", label: "Monto", type: "number", required: true },
    { key: "frequency", label: "Frecuencia", type: "select", options: asOptions(FREQUENCY_LABELS), default: "monthly" },
    { key: "notes", label: "Notas", type: "textarea", full: true },
  ];

  const openCreate = () => { setEditing(null); setOpen(true); };
  const openEdit = (i) => { setEditing(i); setOpen(true); };

  return (
    <Panel
      title="Ingresos"
      subtitle="INCOME STREAMS"
      count={incomes.length}
      addLabel="Ingreso"
      onAdd={openCreate}
      testId="incomes-panel"
    >
      {incomes.length === 0 && <EmptyState hint="Sin ingresos registrados." />}
      {incomes.map((i) => (
        <EntityRow key={i.income_id} onClick={() => openEdit(i)} testId={`income-row-${i.income_id}`}>
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-emerald-500/20 border border-emerald-500/30"
          >
            <ChartLineUp size={18} weight="fill" className="text-emerald-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <div className="text-sm font-heading font-bold text-white truncate">{i.name}</div>
              <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${i.scope === "business" ? "bg-purple-500/20 text-purple-300" : "bg-white/8 text-white/50"}`}>
                {i.scope === "business" ? "NEG" : "PER"}
              </span>
            </div>
            <div className="text-[11px] font-mono text-white/50 truncate">
              {INCOME_SOURCE_LABELS[i.source_type] || i.source_type} · {FREQUENCY_LABELS[i.frequency]}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="font-mono font-bold tabular-nums text-emerald-400">
              +{fmtMoney(i.amount)}
            </div>
            <div className="text-[10px] font-mono text-white/40 uppercase tracking-wider">
              {FREQUENCY_LABELS[i.frequency]}
            </div>
          </div>
        </EntityRow>
      ))}
      <EntityDialog
        open={open}
        onOpenChange={setOpen}
        entity={editing}
        schema={schema}
        endpoint="/finance/incomes"
        idField="income_id"
        title={editing ? "Editar ingreso" : "Nuevo ingreso"}
        defaults={{ scope: scope === "business" ? "business" : "personal" }}
        onSaved={onChange}
        onDeleted={onChange}
      />
    </Panel>
  );
}

// ---------- Expenses ----------
export function ExpensesPanel({ expenses, scope, onChange }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const { categories, byKey } = useCategories();

  // Build category options from dynamic user catalog (fallback to hardcoded labels)
  const categoryOptions = categories.length > 0
    ? categories.map((c) => ({ value: c.key, label: c.label }))
    : Object.entries(CATEGORY_LABELS).map(([value, label]) => ({ value, label }));

  const schema = [
    { key: "name", label: "Nombre", type: "text", required: true, full: true, placeholder: "Renta" },
    { key: "category", label: "Categoría", type: "select", options: categoryOptions, default: "other" },
    { key: "scope", label: "Ámbito", type: "select", options: [{value:"personal",label:"Personal"},{value:"business",label:"Negocio"}], default: scope === "business" ? "business" : "personal" },
    { key: "amount", label: "Monto", type: "number", required: true },
    { key: "frequency", label: "Frecuencia", type: "select", options: asOptions(FREQUENCY_LABELS), default: "monthly" },
    { key: "is_fixed", label: "Fijo?", type: "toggle", default: true, hint: "Fijo = renta, no cambia." },
    { key: "notes", label: "Notas", type: "textarea", full: true },
  ];

  const openCreate = () => { setEditing(null); setOpen(true); };
  const openEdit = (e) => { setEditing(e); setOpen(true); };

  return (
    <Panel
      title="Gastos"
      subtitle="EXPENSES · MONTHLY BURN"
      count={expenses.length}
      addLabel="Gasto"
      onAdd={openCreate}
      testId="expenses-panel"
    >
      {expenses.length === 0 && <EmptyState hint="Sin gastos registrados." />}
      {expenses.map((e) => {
        const catMeta = byKey[e.category];
        const catLabel = catMeta?.label || CATEGORY_LABELS[e.category] || e.category;
        const catColor = catMeta?.color || "#F97316";
        const catIcon = catMeta?.icon;
        return (
          <EntityRow key={e.expense_id} onClick={() => openEdit(e)} testId={`expense-row-${e.expense_id}`}>
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
              style={{ backgroundColor: `${catColor}22`, border: `1px solid ${catColor}55` }}
            >
              {catIcon ? (
                <CategoryIcon name={catIcon} size={16} style={{ color: catColor }} />
              ) : (
                <span className="font-mono font-bold text-xs" style={{ color: catColor }}>
                  {catLabel.slice(0, 3).toUpperCase()}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="text-sm font-heading font-bold text-white truncate">{e.name}</div>
                <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${e.scope === "business" ? "bg-purple-500/20 text-purple-300" : "bg-white/8 text-white/50"}`}>
                  {e.scope === "business" ? "NEG" : "PER"}
                </span>
                {e.is_fixed && (
                  <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300">
                    FIJO
                  </span>
                )}
              </div>
              <div className="text-[11px] font-mono text-white/50 truncate">
                {catLabel} · {FREQUENCY_LABELS[e.frequency]}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="font-mono font-bold tabular-nums text-white">
                -{fmtMoney(e.amount)}
              </div>
              <div className="text-[10px] font-mono text-white/40 uppercase tracking-wider">
                {FREQUENCY_LABELS[e.frequency]}
              </div>
            </div>
          </EntityRow>
        );
      })}
      <EntityDialog
        open={open}
        onOpenChange={setOpen}
        entity={editing}
        schema={schema}
        endpoint="/finance/expenses"
        idField="expense_id"
        title={editing ? "Editar gasto" : "Nuevo gasto"}
        defaults={{ scope: scope === "business" ? "business" : "personal", is_fixed: true }}
        onSaved={onChange}
        onDeleted={onChange}
      />
    </Panel>
  );
}
